import React, { useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment-timezone';
import 'react-datepicker/dist/react-datepicker.css';
import { post_api as postApi } from '../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../utils/urls';
import { getTimezoneAbbr } from '../../../../constants';
import ConfirmSessionDetails from '../../sessions/request_session_dashboard/ConfirmSessionDetails';
import './request_session.css';

/**
 * Request a Session — care-team popup, v2 (PROD-9033, Figma "time-first-booking").
 *
 * v2 changes vs v1:
 *  - Goals removed from the UI; the backend matches on the STUDENT's own goals.
 *  - Date, time & duration collapse into one "Student Date, Time & Duration" section.
 *  - "Who should receive this request" picks the audience: All / Trial / Custom teachers.
 *    Gender + Language show only for All & Trial; Custom takes a pasted slug list instead.
 *  - "How long should the session be accepted?" sets a request time-limit (5m/15m/1h/custom).
 *  - A match-preview banner shows matched / hidden / will-receive counts; each number opens
 *    the corresponding teacher list.
 *
 * On Confirm it POSTs to /v2/session_request/care/create (goserver, ops token).
 */

const DURATION_OPTIONS = [30, 45, 60, 90];
const DEFAULT_DURATION = 60;

const GENDER_OPTIONS = [
  { value: 'NO_PREFERENCE', label: 'No Preference' },
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' }
];

// Multi-select: top-level languages + an "Indian Languages" group (per PRD).
const LANGUAGE_OPTIONS = ['English', 'Spanish', 'French'];
const INDIAN_LANGUAGE_OPTIONS = ['Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam'];

// Audience for the broadcast. ALL/TRIAL match on goals+gender+language; CUSTOM sends to a
// pasted slug list with no goal/gender/language matching.
const RECEIVE_OPTIONS = [
  { value: 'ALL', label: 'All Teacher' },
  { value: 'TRIAL', label: 'Trial Teacher', hint: '(Teacher who accepts trials)' },
  { value: 'CUSTOM', label: 'Custom Teachers list' }
];

// Request auto-closes after this many minutes if unaccepted. CUSTOM lets care enter hh/mm.
const TIME_LIMIT_OPTIONS = [
  { value: 5, label: '5 Min' },
  { value: 15, label: '15 Min' },
  { value: 60, label: '1 hr' },
  { value: 'CUSTOM', label: 'Custom' }
];
const DEFAULT_TIME_LIMIT = 15;

// The four match-preview counts and the teacher-list view each maps to.
const COUNT_FIELDS = [
  { key: 'matching', label: 'Matching Teacher', title: 'Matching Teacher List' },
  { key: 'no_replacement', label: 'No replacement', title: 'Hidden from Replacement' },
  { key: 'no_trial', label: 'No trial', title: 'Hidden from Trial Users' },
  { key: 'will_receive', label: 'Will receive request', title: 'Will Receive Request' }
];

const field_label_style = {
  display: 'block',
  fontSize: 13.5,
  fontWeight: 500,
  marginBottom: 8,
  color: 'var(--text-label)'
};

const field_base = {
  width: '100%',
  height: 46,
  boxSizing: 'border-box',
  padding: '0 16px',
  borderRadius: 8,
  border: '1px solid var(--input-border)',
  background: 'var(--input-bg)',
  fontSize: 15,
  fontWeight: 500,
  outline: 'none',
  appearance: 'none',
  WebkitAppearance: 'none',
  cursor: 'pointer'
};

const primary_btn = {
  minWidth: 120,
  padding: '14px 22px',
  borderRadius: 14,
  border: 'none',
  background: 'var(--brand-primary)',
  color: 'var(--text-inverse)',
  fontSize: 17,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0px 2px 4px 0px rgba(248, 102, 36, 0.15)'
};

// Dropdown panels overlay the fields below (absolute) instead of pushing them down. The parent
// field container is position:relative and gets a raised z-index while open (see the field wrappers).
const panel_style = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 10,
  zIndex: 30,
  padding: '25px 25px 27px',
  borderRadius: 14,
  background: 'var(--surface-card)',
  border: '1px solid var(--picker-panel-border)',
  boxShadow: '0 2px 10px 0 rgba(188, 188, 188, 0.25)'
};

// Calendar month-nav chevron button (custom header) — thin grey chevrons, dimmed when disabled.
const cal_nav_btn = (disabled) => ({
  border: 'none',
  background: 'transparent',
  padding: '0 2px',
  fontSize: 22,
  lineHeight: 1,
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? 'var(--border-default)' : 'var(--text-muted)'
});

const chevron_icon = (open) => (
  <span
    aria-hidden
    style={{
      width: 9,
      height: 9,
      marginLeft: 8,
      flexShrink: 0,
      transform: open ? 'translateY(3px) rotate(-135deg)' : 'rotate(45deg)',
      borderRight: '2px solid var(--input-placeholder)',
      borderBottom: '2px solid var(--input-placeholder)'
    }}
  />
);

function FieldLabel({ children, hint }) {
  return (
    <span style={field_label_style}>
      {children}
      {hint ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> {hint}</span> : null}
    </span>
  );
}

// A trigger that looks like a select but opens an inline panel below it.
function SelectTrigger({ open, placeholder, value, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...field_base,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'left',
        color: value ? 'var(--input-text)' : 'var(--input-placeholder)',
        borderColor: open ? 'var(--input-focus-border)' : 'var(--input-border)'
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value || placeholder}
      </span>
      {chevron_icon(open)}
    </button>
  );
}

// Circular radio (single-select), Figma "Form / Radio Button" (22px). Unselected: a flat light-grey
// disc (no ring). Selected: white base + 2px orange ring + 7px orange dot + soft shadow. Used by the
// "teacher type" and "time limit" option lists.
function Radio({ checked }) {
  if (checked) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          flexShrink: 0,
          borderRadius: '50%',
          background: 'var(--surface-card)',
          border: '2px solid var(--brand-primary)',
          boxShadow: '0px 2px 4px 0px rgba(188, 188, 188, 0.2)'
        }}
      >
        <span
          style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--brand-primary)' }}
        />
      </span>
    );
  }
  return (
    <span
      style={{
        width: 22,
        height: 22,
        flexShrink: 0,
        borderRadius: '50%',
        background: 'var(--radio-unselected-bg)'
      }}
    />
  );
}

// Exact Figma check glyph (10×10). stroke=currentColor so it inherits the brand orange and
// stays theme-aware.
const CHECK_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path
      d="M8.33341 2.5L3.75008 7.08333L1.66675 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Rounded-square checkbox (Figma dev spec: 18×18, 7px radius) — empty grey when unselected,
// soft-orange fill + orange ✓ when selected. Used by the exact-time slots & session duration.
function CheckBox({ checked }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        flexShrink: 0,
        borderRadius: 7,
        background: checked ? 'var(--check-selected-bg)' : 'var(--check-bg)',
        border: `1px solid ${checked ? 'var(--check-selected-border)' : 'var(--check-border)'}`,
        color: 'var(--brand-primary)'
      }}
    >
      {checked ? CHECK_ICON : ''}
    </span>
  );
}

function RadioRow({ label, hint, checked, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '8px 0',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left'
      }}
    >
      <Radio checked={checked} />
      <span
        style={{
          fontSize: 13.5,
          fontWeight: 500,
          lineHeight: '22px',
          color: checked ? 'var(--brand-primary)' : 'var(--text-label)'
        }}
      >
        {label}
        {hint ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> {hint}</span> : null}
      </span>
    </button>
  );
}

function CheckRow({ label, checked, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '9px 4px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left'
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          flexShrink: 0,
          borderRadius: 6,
          background: checked ? 'var(--brand-primary)' : 'var(--surface-sunken)',
          border: `1px solid ${checked ? 'var(--brand-primary)' : 'var(--border-default)'}`,
          color: 'var(--text-inverse)',
          fontSize: 12,
          lineHeight: 1
        }}
      >
        {checked ? '✓' : ''}
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: checked ? 'var(--brand-primary)' : 'var(--text-primary)'
        }}
      >
        {label}
      </span>
    </button>
  );
}

const SLOT_INTERVAL_MIN = 30;
const SLOTS_PER_DAY = (24 * 60) / SLOT_INTERVAL_MIN;

const textarea_style = {
  width: '100%',
  minHeight: 96,
  boxSizing: 'border-box',
  padding: '14px 18px',
  borderRadius: 8,
  border: '1px solid var(--input-bg)',
  background: 'var(--input-bg)',
  color: 'var(--input-text)',
  fontSize: 15,
  fontWeight: 500,
  resize: 'none',
  outline: 'none',
  fontFamily: 'inherit'
};

// Humanize the auto-close window for the helper line / summary.
const humanize_minutes = (mins) => {
  if (!mins || mins <= 0) return '—';
  if (mins % 60 === 0) {
    const h = mins / 60;
    return `${h} hour${h === 1 ? '' : 's'}`;
  }
  if (mins > 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  }
  return `${mins} minutes`;
};

const parse_teacher_slugs = (raw) =>
  (raw || '')
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

function RequestSession({
  details = {},
  student_uuid,
  iana_timezone,
  initialValues = {},
  initialStep = 'form',
  requireChange = false,
  warnings = [],
  close,
  set_notification_variable
}) {
  const [step, setStep] = useState(initialStep); // 'form' | 'confirm' | 'teacherList'
  const [submitting, setSubmitting] = useState(false);

  // Date / time / duration (one collapsible section).
  const [sessionDate, setSessionDate] = useState(initialValues.sessionDate || null);
  const [dateOpen, setDateOpen] = useState(false);
  const [pickedDate, setPickedDate] = useState(initialValues.sessionDate || null);
  const [duration, setDuration] = useState(initialValues.duration || DEFAULT_DURATION);
  const dateRef = useRef(null);

  // Audience.
  // Start UNSELECTED on a fresh create (PROD-9033 QA) — care must consciously pick the audience;
  // resend pre-fills from initialValues. Empty string renders the "Choose a type" placeholder.
  const [receiveMode, setReceiveMode] = useState(initialValues.receiveMode || '');
  const [receiveOpen, setReceiveOpen] = useState(false);
  const receiveRef = useRef(null);
  const [customTeachers, setCustomTeachers] = useState(initialValues.customTeachers || '');

  // Match filters (ALL / TRIAL only).
  const [coachGender, setCoachGender] = useState(initialValues.coachGender || 'NO_PREFERENCE');
  const [languages, setLanguages] = useState(initialValues.languages || []);
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const languagesRef = useRef(null);

  // Request time-limit.
  // Also start UNSELECTED on a fresh create (placeholder shown); resend pre-fills from initialValues.
  const [timeLimit, setTimeLimit] = useState(initialValues.timeLimit ?? '');
  const [timeLimitOpen, setTimeLimitOpen] = useState(false);
  const [customHours, setCustomHours] = useState(initialValues.customHours || '');
  const [customMins, setCustomMins] = useState(initialValues.customMins || '');
  const timeLimitRef = useRef(null);

  const [specificNeeds, setSpecificNeeds] = useState(initialValues.specificNeeds || '');

  // Match preview (from backend) + which count-list the teacherList step shows.
  const [counts, setCounts] = useState(null);
  const [listKind, setListKind] = useState('matching');

  const isCustom = receiveMode === 'CUSTOM';

  const studentZone = iana_timezone || details.iana_timezone || moment.tz.guess();
  const zoneAbbr = getTimezoneAbbr(studentZone) || moment.tz(studentZone).zoneAbbr();

  const timeLimitMinutes = useMemo(() => {
    if (timeLimit === 'CUSTOM') {
      return (parseInt(customHours, 10) || 0) * 60 + (parseInt(customMins, 10) || 0);
    }
    return Number(timeLimit);
  }, [timeLimit, customHours, customMins]);

  useEffect(() => {
    if (!dateOpen) return undefined;
    const on_click_outside = (e) => {
      if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false);
    };
    document.addEventListener('mousedown', on_click_outside);
    return () => document.removeEventListener('mousedown', on_click_outside);
  }, [dateOpen]);

  useEffect(() => {
    if (!receiveOpen) return undefined;
    const on_click_outside = (e) => {
      if (receiveRef.current && !receiveRef.current.contains(e.target)) setReceiveOpen(false);
    };
    document.addEventListener('mousedown', on_click_outside);
    return () => document.removeEventListener('mousedown', on_click_outside);
  }, [receiveOpen]);

  useEffect(() => {
    if (!languagesOpen) return undefined;
    const on_click_outside = (e) => {
      if (languagesRef.current && !languagesRef.current.contains(e.target)) setLanguagesOpen(false);
    };
    document.addEventListener('mousedown', on_click_outside);
    return () => document.removeEventListener('mousedown', on_click_outside);
  }, [languagesOpen]);

  useEffect(() => {
    if (!timeLimitOpen) return undefined;
    const on_click_outside = (e) => {
      if (timeLimitRef.current && !timeLimitRef.current.contains(e.target)) setTimeLimitOpen(false);
    };
    document.addEventListener('mousedown', on_click_outside);
    return () => document.removeEventListener('mousedown', on_click_outside);
  }, [timeLimitOpen]);

  // 30-min start-time slots for the picked date, in the student's wall clock (past slots hidden
  // when the picked date is today).
  const time_slots = useMemo(() => {
    if (!pickedDate) return [];
    const startOfDay = moment(pickedDate).startOf('day');
    const now = moment();
    const isToday = startOfDay.isSame(now, 'day');
    const slots = [];
    for (let i = 0; i < SLOTS_PER_DAY; i += 1) {
      const t = startOfDay.clone().add(i * SLOT_INTERVAL_MIN, 'minutes');
      if (isToday && t.isBefore(now)) continue;
      slots.push({ date: t.toDate(), label: t.format('h:mma') });
    }
    return slots;
  }, [pickedDate]);

  // Show the picked wall-clock time in IST (PRD) as a helper line.
  const istTime = useMemo(() => {
    if (!sessionDate) return '';
    const m = moment.tz(moment(sessionDate).format('YYYY-MM-DDTHH:mm'), studentZone);
    if (!m.isValid()) return '';
    return m.clone().tz('Asia/Kolkata').format('D MMMM - h:mm A [IST]');
  }, [sessionDate, studentZone]);

  const dt_summary = sessionDate
    ? `${moment(sessionDate).format('MMM D, YYYY · h:mm A')} ${zoneAbbr} · ${duration} min`
    : '';

  const receiveLabel = RECEIVE_OPTIONS.find((o) => o.value === receiveMode)?.label;
  // Show the option's label ("Custom") in the trigger — the entered hh/mm surfaces in the
  // "auto-close after…" helper line and the confirm summary, not the trigger.
  const timeLimitLabel = TIME_LIMIT_OPTIONS.find((o) => o.value === timeLimit)?.label;
  const genderLabel = GENDER_OPTIONS.find((g) => g.value === coachGender)?.label;

  const toggle_language = (l) =>
    setLanguages((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));

  // Fetch match-preview counts whenever the matching criteria change (ALL / TRIAL only — CUSTOM
  // sends to an explicit list, so there is nothing to preview). Endpoint lands in the PROD-9033
  // backend phase; until then it errors and the banner simply stays hidden.
  useEffect(() => {
    const uuid = student_uuid || details.uuid || details.student_uuid;
    // No preview until an audience is chosen (fresh create starts unselected).
    if (isCustom || !receiveMode || !uuid || !sessionDate) {
      setCounts(null);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      const url = getApiUrl('/v2/session_request/care/match_preview', false, true);
      const payload = {
        student_uuid: uuid,
        start_time: moment
          .tz(moment(sessionDate).format('YYYY-MM-DDTHH:mm'), studentZone)
          .valueOf(),
        duration: Number(duration),
        teacher_selection_mode: receiveMode,
        coach_gender: coachGender,
        language: languages.length ? languages.join(', ') : null
      };
      postApi(url, payload, true)
        .then((resp) => {
          if (!cancelled) setCounts(resp.data?.data || null);
        })
        .catch(() => {
          if (!cancelled) setCounts(null);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [isCustom, sessionDate, duration, receiveMode, coachGender, languages, studentZone]);

  // Resend flow: a resend must change at least one detail (PROD-8795).
  const is_unchanged_from_initial = () => {
    const iv = initialValues || {};
    const sameDate =
      (!sessionDate && !iv.sessionDate) ||
      (!!sessionDate && !!iv.sessionDate && +new Date(sessionDate) === +new Date(iv.sessionDate));
    const sameDuration = Number(duration) === Number(iv.duration ?? DEFAULT_DURATION);
    const sameMode = receiveMode === (iv.receiveMode || 'ALL');
    const sameGender = coachGender === (iv.coachGender || 'NO_PREFERENCE');
    const sameTimeLimit =
      Number(timeLimitMinutes) === Number(iv.timeLimitMinutes ?? DEFAULT_TIME_LIMIT);
    const norm = (arr) =>
      [...(arr || [])]
        .map((x) => String(x).trim())
        .sort()
        .join('|');
    const sameLanguages = norm(languages) === norm(iv.languages);
    const sameCustom = (customTeachers || '').trim() === (iv.customTeachers || '').trim();
    const sameNeeds = (specificNeeds || '').trim() === (iv.specificNeeds || '').trim();
    return (
      sameDate &&
      sameDuration &&
      sameMode &&
      sameGender &&
      sameTimeLimit &&
      sameLanguages &&
      sameCustom &&
      sameNeeds
    );
  };

  const go_to_confirm = () => {
    if (!sessionDate) {
      set_notification_variable?.(true, 'error', 'Please select a date and time for the session.');
      return;
    }
    if (!receiveMode) {
      set_notification_variable?.(true, 'error', 'Please select who should receive this request.');
      return;
    }
    if (isCustom && !parse_teacher_slugs(customTeachers).length) {
      set_notification_variable?.(true, 'error', 'Please paste at least one teacher slug.');
      return;
    }
    if (!timeLimit || !timeLimitMinutes || timeLimitMinutes <= 0) {
      set_notification_variable?.(
        true,
        'error',
        'Please select how long the session should be accepted.'
      );
      return;
    }
    if (requireChange && is_unchanged_from_initial()) {
      set_notification_variable?.(
        true,
        'error',
        'You haven’t changed anything. Please edit a detail before resending.'
      );
      return;
    }
    setStep('confirm');
  };

  const on_confirm = () => {
    const uuid = student_uuid || details.uuid || details.student_uuid;
    if (!uuid) {
      set_notification_variable?.(
        true,
        'error',
        'Missing student. Please reopen from the profile.'
      );
      return;
    }
    if (submitting) return;
    const trimmed_needs = specificNeeds.trim();
    const payload = {
      student_uuid: uuid,
      start_time: moment.tz(moment(sessionDate).format('YYYY-MM-DDTHH:mm'), studentZone).valueOf(),
      duration: Number(duration),
      teacher_selection_mode: receiveMode,
      // ALL/TRIAL match on gender/language; CUSTOM sends to an explicit slug list instead.
      coach_gender: isCustom ? null : coachGender,
      language: isCustom || !languages.length ? null : languages.join(', '),
      custom_teacher_slugs: isCustom ? parse_teacher_slugs(customTeachers) : null,
      // Request auto-closes after this window if unaccepted.
      time_limit_minutes: timeLimitMinutes,
      // Goals dropped from the UI in v2 — the backend matches on the student's own goals.
      specific_needs: trimmed_needs || null
    };
    setSubmitting(true);
    const url = getApiUrl('/v2/session_request/care/create', false, true);
    postApi(url, payload, true)
      .then((resp) => {
        const data = resp.data?.data || {};
        const count = data.teachers_count || 0;
        set_notification_variable?.(
          true,
          'success',
          `Session request sent to ${count} matched teacher${count === 1 ? '' : 's'}.`
        );
        close?.();
      })
      .catch((e) => {
        setSubmitting(false);
        let msg;
        try {
          msg = e.response.data.message || e.response.data.reason;
        } catch (err) {
          msg = 'Could not create the session request. Please try again.';
        }
        set_notification_variable?.(true, 'error', msg);
      });
  };

  // ---- Matching-teacher-list view (opened from a count in the preview banner) ----
  if (step === 'teacherList') {
    const field = COUNT_FIELDS.find((f) => f.key === listKind) || COUNT_FIELDS[0];
    const list = (counts?.lists && counts.lists[listKind]) || [];
    return (
      <div>
        <button
          type="button"
          onClick={() => setStep('form')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            border: 'none',
            background: 'transparent',
            color: 'var(--text-muted)',
            fontSize: 15,
            fontWeight: 500,
            cursor: 'pointer',
            padding: 0,
            marginBottom: 18
          }}
        >
          <span aria-hidden>←</span> Back
        </button>
        <p
          style={{
            fontSize: 19,
            fontWeight: 600,
            margin: '0 0 18px',
            color: 'var(--text-primary)'
          }}
        >
          {field.title}
        </p>
        {list.length ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {list.map((t) => (
              <div
                key={t.uuid || t.slug || t.name}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0' }}
              >
                <img
                  src={t.profile_photo || t.photo}
                  alt=""
                  onError={(e) => {
                    e.target.style.visibility = 'hidden';
                  }}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    background: 'var(--surface-sunken)',
                    flexShrink: 0
                  }}
                />
                <span style={{ fontSize: 16, color: 'var(--text-primary)' }}>
                  {t.name || `${t.first_name || ''} ${t.last_name || ''}`.trim()}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>No teachers in this list.</p>
        )}
      </div>
    );
  }

  if (step === 'confirm') {
    const confirm_row = {
      student_zone_abbr: zoneAbbr,
      student_time: `${moment(sessionDate).format('D MMMM')} - ${moment(sessionDate).format(
        'h:mm A'
      )} ${zoneAbbr}`,
      teacher_time: istTime,
      duration: `${duration} Mins`,
      language: !isCustom && languages.length ? languages.join(', ') : '-',
      gender: isCustom ? '-' : genderLabel,
      // "How long the session can be accepted" in the confirm popup (PROD-9473) — was showing "-".
      time_limit: timeLimitMinutes > 0 ? humanize_minutes(timeLimitMinutes) : '-',
      goals: [],
      comment: specificNeeds || '-'
    };
    return (
      <ConfirmSessionDetails
        row={confirm_row}
        confirming={submitting}
        onEdit={() => setStep('form')}
        onConfirm={on_confirm}
      />
    );
  }

  return (
    <div>
      <p
        style={{
          fontSize: 19,
          lineHeight: '22px',
          fontWeight: 600,
          margin: 0,
          color: 'var(--text-primary)'
        }}
      >
        Request a Session
      </p>
      <p
        style={{
          fontSize: 14.5,
          fontWeight: 500,
          margin: '7px 0 20px',
          color: 'var(--text-muted)'
        }}
      >
        Select the options below to create a new session request
      </p>

      {warnings.length ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            margin: '0 0 20px',
            padding: '12px 16px',
            borderRadius: 8,
            background: 'var(--state-warning-bg)',
            border: '1px solid var(--state-warning)',
            color: 'var(--state-warning-text)'
          }}
        >
          {warnings.map((w) => (
            <div key={w} style={{ display: 'flex', gap: 8, fontSize: 13, fontWeight: 500 }}>
              <span aria-hidden>⚠️</span>
              <span>{w}</span>
            </div>
          ))}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* --- Student Date, Time & Duration --- */}
        <div ref={dateRef} style={{ position: 'relative', zIndex: dateOpen ? 20 : 'auto' }}>
          <FieldLabel hint={`(All times in ${zoneAbbr})`}>
            Student Date, Time &amp; Duration
          </FieldLabel>
          <SelectTrigger
            open={dateOpen}
            placeholder="Choose a type"
            value={dt_summary}
            onClick={() => setDateOpen((o) => !o)}
          />
          {dateOpen ? (
            <div style={panel_style}>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px', paddingRight: 20 }}>
                  <DatePicker
                    inline
                    selected={pickedDate || sessionDate}
                    onChange={(d) => setPickedDate(d)}
                    minDate={new Date()}
                    calendarStartDay={1}
                    formatWeekDay={(name) => name.slice(0, 2)}
                    calendarClassName="rs-datepicker"
                    renderCustomHeader={({
                      monthDate,
                      decreaseMonth,
                      increaseMonth,
                      prevMonthButtonDisabled,
                      nextMonthButtonDisabled
                    }) => (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '2px 6px 12px'
                        }}
                      >
                        <span
                          style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}
                        >
                          {moment(monthDate).format('MMMM YYYY')}
                        </span>
                        <div style={{ display: 'flex', gap: 14 }}>
                          <button
                            type="button"
                            aria-label="Previous month"
                            onClick={decreaseMonth}
                            disabled={prevMonthButtonDisabled}
                            style={cal_nav_btn(prevMonthButtonDisabled)}
                          >
                            ‹
                          </button>
                          <button
                            type="button"
                            aria-label="Next month"
                            onClick={increaseMonth}
                            disabled={nextMonthButtonDisabled}
                            style={cal_nav_btn(nextMonthButtonDisabled)}
                          >
                            ›
                          </button>
                        </div>
                      </div>
                    )}
                  />
                </div>
                <div
                  style={{
                    flex: '1 1 200px',
                    minWidth: 180,
                    paddingLeft: 20,
                    borderLeft: '1px solid var(--picker-panel-border)'
                  }}
                >
                  <p
                    style={{
                      margin: '0 0 12px',
                      fontSize: 12,
                      fontWeight: 500,
                      color: 'var(--picker-caption)'
                    }}
                  >
                    Or choose an exact time (All times in {zoneAbbr})
                  </p>
                  <div
                    className="flex flex-col gap-[8px]"
                    style={{ maxHeight: 300, overflowY: 'auto', paddingRight: 4 }}
                  >
                    {pickedDate ? (
                      time_slots.map((slot) => {
                        const selected =
                          !!sessionDate && moment(sessionDate).isSame(slot.date, 'minute');
                        return (
                          <button
                            key={slot.label}
                            type="button"
                            className="rs-time-slot"
                            onClick={() => setSessionDate(slot.date)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              padding: '0px 0px',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              margin: 0
                            }}
                          >
                            <span
                              className="rs-time-slot-label"
                              style={{
                                fontSize: 14.5,
                                fontWeight: 550,
                                lineHeight: '22px',
                                color: selected ? 'var(--brand-primary)' : 'var(--time-slot-text)'
                              }}
                            >
                              {slot.label}
                            </span>
                            <CheckBox checked={selected} />
                          </button>
                        );
                      })
                    ) : (
                      <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Pick a date first.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Divider spans the full panel width (negative margins cancel the 14px panel
                  padding), then the content re-pads by 14px — matches Figma's edge-to-edge line. */}
              <div
                style={{
                  margin: '14px -25px 0',
                  padding: '14px 25px 0',
                  borderTop: '1px solid var(--picker-panel-border)'
                }}
              >
                <p
                  style={{
                    margin: '0 0 12px',
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--picker-caption)'
                  }}
                >
                  Session Duration
                </p>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 14,
                    alignContent: 'flex-start'
                  }}
                >
                  {DURATION_OPTIONS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDuration(d)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 10,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        padding: 0,
                        margin: 0
                      }}
                    >
                      <CheckBox checked={duration === d} />
                      <span
                        style={{
                          fontSize: 13.5,
                          fontWeight: 500,
                          lineHeight: '22px',
                          color: duration === d ? 'var(--brand-primary)' : 'var(--text-label)'
                        }}
                      >
                        {d} minutes
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
          {istTime ? (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Indian time {istTime}
            </p>
          ) : null}
        </div>

        {/* --- Who should receive this request --- */}
        <div ref={receiveRef} style={{ position: 'relative', zIndex: receiveOpen ? 20 : 'auto' }}>
          <FieldLabel>Who should receive this request</FieldLabel>
          <SelectTrigger
            open={receiveOpen}
            value={receiveLabel}
            placeholder="Choose a type"
            onClick={() => setReceiveOpen((o) => !o)}
          />
          {receiveOpen ? (
            <div style={panel_style}>
              <p
                style={{
                  margin: '0 0 6px',
                  fontSize: 17,
                  fontWeight: 700,
                  color: 'var(--text-primary)'
                }}
              >
                Select Teacher Type
              </p>
              {RECEIVE_OPTIONS.map((o) => (
                <RadioRow
                  key={o.value}
                  label={o.label}
                  hint={o.hint}
                  checked={receiveMode === o.value}
                  onClick={() => {
                    setReceiveMode(o.value);
                    setReceiveOpen(false);
                  }}
                />
              ))}
            </div>
          ) : null}
        </div>

        {/* --- Custom slug list (CUSTOM) OR gender+language (ALL/TRIAL); hidden until an
            audience is chosen (fresh create starts unselected). --- */}
        {!receiveMode ? null : isCustom ? (
          <div>
            <FieldLabel>Paste teacher list (One per line or comma separated)</FieldLabel>
            <textarea
              style={textarea_style}
              value={customTeachers}
              onChange={(e) => setCustomTeachers(e.target.value)}
              placeholder="Type here... (Optional)"
            />
            <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              You can get the list from teacher finder or Teacher Match and paste here.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 30px' }}>
            <div>
              <FieldLabel>Gender of coach</FieldLabel>
              <div style={{ position: 'relative' }}>
                <select
                  style={{ ...field_base, color: 'var(--input-text)' }}
                  value={coachGender}
                  onChange={(e) => setCoachGender(e.target.value)}
                >
                  {GENDER_OPTIONS.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </select>
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-25%) rotate(45deg)',
                    width: 8,
                    height: 8,
                    borderRight: '2px solid var(--input-placeholder)',
                    borderBottom: '2px solid var(--input-placeholder)',
                    pointerEvents: 'none'
                  }}
                />
              </div>
            </div>
            <div ref={languagesRef}>
              <FieldLabel>Language spoken</FieldLabel>
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setLanguagesOpen((o) => !o)}
                  style={{
                    ...field_base,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    color: languages.length ? 'var(--input-text)' : 'var(--input-placeholder)',
                    borderColor: languagesOpen ? 'var(--input-focus-border)' : 'var(--input-border)'
                  }}
                >
                  <span
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {languages.length ? languages.join(', ') : 'Select Language'}
                  </span>
                  {chevron_icon(languagesOpen)}
                </button>
                {languagesOpen ? (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      right: 0,
                      zIndex: 30,
                      maxHeight: 320,
                      overflowY: 'auto',
                      padding: '14px 16px',
                      borderRadius: 12,
                      background: 'var(--surface-card)',
                      border: '1px solid var(--border-default)',
                      boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.12)'
                    }}
                  >
                    {LANGUAGE_OPTIONS.map((l) => (
                      <CheckRow
                        key={l}
                        label={l}
                        checked={languages.includes(l)}
                        onToggle={() => toggle_language(l)}
                      />
                    ))}
                    <p
                      style={{
                        margin: '10px 0 2px',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--text-muted)'
                      }}
                    >
                      Indian Languages
                    </p>
                    {INDIAN_LANGUAGE_OPTIONS.map((l) => (
                      <CheckRow
                        key={l}
                        label={l}
                        checked={languages.includes(l)}
                        onToggle={() => toggle_language(l)}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* --- Request time limit --- */}
        <div
          ref={timeLimitRef}
          style={{ position: 'relative', zIndex: timeLimitOpen ? 20 : 'auto' }}
        >
          <FieldLabel>How long should the session be accepted?</FieldLabel>
          <SelectTrigger
            open={timeLimitOpen}
            value={timeLimitLabel}
            placeholder="Choose a type"
            onClick={() => setTimeLimitOpen((o) => !o)}
          />
          {timeLimitOpen ? (
            <div style={panel_style}>
              <p
                style={{
                  margin: '0 0 6px',
                  fontSize: 17,
                  fontWeight: 700,
                  color: 'var(--text-primary)'
                }}
              >
                Select Time limit
              </p>
              {TIME_LIMIT_OPTIONS.map((o) => (
                <RadioRow
                  key={o.value}
                  label={o.label}
                  checked={timeLimit === o.value}
                  onClick={() => {
                    setTimeLimit(o.value);
                    if (o.value !== 'CUSTOM') setTimeLimitOpen(false);
                  }}
                />
              ))}
              {timeLimit === 'CUSTOM' ? (
                <div style={{ display: 'flex', gap: 15, marginTop: 16, alignItems: 'flex-start' }}>
                  <div>
                    <FieldLabel>Enter Hour</FieldLabel>
                    <input
                      type="number"
                      min="0"
                      style={{
                        ...field_base,
                        width: 101,
                        border: 'none',
                        color: 'var(--input-text)',
                        cursor: 'text'
                      }}
                      value={customHours}
                      onChange={(e) => setCustomHours(e.target.value)}
                      placeholder="Hour"
                    />
                  </div>
                  <div>
                    <FieldLabel>Enter Mins</FieldLabel>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      style={{
                        ...field_base,
                        width: 101,
                        border: 'none',
                        color: 'var(--input-text)',
                        cursor: 'text'
                      }}
                      value={customMins}
                      onChange={(e) => setCustomMins(e.target.value)}
                      placeholder="Mins"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          {timeLimit && timeLimitMinutes > 0 ? (
            <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Request will auto-close after {humanize_minutes(timeLimitMinutes)} if not accepted
            </p>
          ) : null}
        </div>

        {/* --- Specific requirements --- */}
        <div>
          <FieldLabel hint="(Optional)">
            Student’s Specific requirements for today’s session?
          </FieldLabel>
          <textarea
            style={textarea_style}
            value={specificNeeds}
            onChange={(e) => setSpecificNeeds(e.target.value)}
            placeholder="Type here... (Optional)"
          />
        </div>

        {/* --- Match-preview counts (ALL / TRIAL) --- */}
        {!isCustom && counts ? (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '6px 14px',
              padding: '12px 16px',
              borderRadius: 8,
              background: 'var(--banner-match-bg)',
              color: 'var(--banner-match-text)',
              fontSize: 13.5,
              fontWeight: 500
            }}
          >
            {COUNT_FIELDS.map((f, i) => (
              <React.Fragment key={f.key}>
                {i > 0 ? <span style={{ opacity: 0.4 }}>|</span> : null}
                <span>
                  {f.label}:{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setListKind(f.key);
                      setStep('teacherList');
                    }}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      cursor: 'pointer',
                      fontSize: 13.5,
                      fontWeight: 700,
                      textDecoration: 'underline',
                      color: 'var(--banner-match-link)'
                    }}
                  >
                    {counts[f.key] ?? 0}
                  </button>
                </span>
              </React.Fragment>
            ))}
          </div>
        ) : null}
      </div>

      <div style={{ marginTop: 26 }}>
        <button type="button" style={primary_btn} onClick={go_to_confirm}>
          Next
        </button>
      </div>
    </div>
  );
}

export default RequestSession;
